import type {
    HistoryBucket,
    PerPersonBucket,
    PerPersonCategorySeries
} from '../../../services/dmMonitoringOverviewHistory';
import {
    chartRow,
    TOTAL_LABEL,
    type BucketCounts,
    type ChartSeries,
    type OverTimeChartData
} from './overTimeRows.ts';

// What each person's section counts: handled notifications/messages, or distinct patients.
export type PerPersonMeasure = 'events' | 'patients';

// Given out in people order, so each person keeps the same colour across buckets.
export const PERSON_PALETTE = [
    '#2C6F67',
    '#3773A8',
    '#5CC6BB',
    '#D97706',
    '#7C3AED',
    '#DB2777',
    '#84CC16',
    '#0EA5E9'
] as const;
export const OTHERS_COLOR = '#9ca3af';
const OTHERS_KEY = 'others';
const OTHERS_NAME = 'Others';
const FORMER_USER = 'Former user';
export const TOTAL_LABELS: Readonly<Record<PerPersonMeasure, string>> = {
    events: TOTAL_LABEL,
    patients: 'Total patients'
};

function windowTotals({ series }: PerPersonCategorySeries): Map<string, number> {
    const totals = new Map<string, number>();
    for (const point of series) {
        for (const [userId, count] of Object.entries(point.counts)) {
            totals.set(userId, (totals.get(userId) ?? 0) + count);
        }
    }
    return totals;
}

// Past the palette, its last slot goes to one grey Others series.
function namedSlots(peopleCount: number): number {
    return peopleCount > PERSON_PALETTE.length ? PERSON_PALETTE.length - 1 : peopleCount;
}

function chartPeople(data: PerPersonCategorySeries): ChartSeries[] {
    const totals = windowTotals(data);
    const active = data.people.filter((person) => (totals.get(person.userId) ?? 0) > 0);
    const named = namedSlots(active.length);
    return active.map((person, index) => ({
        key: person.userId,
        label: person.name ?? FORMER_USER,
        color: index < named ? PERSON_PALETTE[index] : OTHERS_COLOR
    }));
}

function chartSegments(people: readonly ChartSeries[]): ChartSeries[] {
    const named = namedSlots(people.length);
    const segments = people.slice(0, named);
    if (named === people.length) return segments;
    return [...segments, { key: OTHERS_KEY, label: OTHERS_NAME, color: OTHERS_COLOR }];
}

function segmentValues(counts: Record<string, number>, people: readonly ChartSeries[]): number[] {
    const values = people.map((person) => counts[person.key] ?? 0);
    const named = namedSlots(people.length);
    if (named === people.length) return values;
    const others = values.slice(named).reduce((sum, count) => sum + count, 0);
    return [...values.slice(0, named), others];
}

function measuredCounts(point: PerPersonBucket, measure: PerPersonMeasure): BucketCounts {
    if (measure === 'events') return point;
    return {
        bucketStart: point.bucketStart,
        total: point.patientTotal ?? 0,
        counts: point.patientCounts ?? {}
    };
}

// People and colours come from events either way, so toggling the measure keeps each colour.
export function buildPerPersonChart(
    data: PerPersonCategorySeries,
    bucket: HistoryBucket,
    measure: PerPersonMeasure
): OverTimeChartData {
    const people = chartPeople(data);
    return {
        series: people,
        segments: chartSegments(people),
        rows: data.series.map((point) => {
            const measured = measuredCounts(point, measure);
            return chartRow(measured, bucket, segmentValues(measured.counts, people));
        }),
        tooltipOrder: 'count',
        totalLabel: TOTAL_LABELS[measure]
    };
}
